import React, { useEffect, useMemo, useState } from "react";
import { FaEdit, FaLayerGroup, FaPlus, FaSearch, FaShieldAlt, FaTrash } from "react-icons/fa";
import api from "../api";
import Modal from "../components/Modal";
import InfoTooltip from "../components/InfoTooltip";
import { useAuth } from "../AuthContext";
import { formatPermissionName } from "../utils/permissions";
import { groupPermissionsByModule } from "../utils/permissionModules";

export default function Groups() {
  const { user } = useAuth();
  const isMasterUser = Boolean(user?.isMaster);
  const [items, setItems] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [form, setForm] = useState({ name: "", permissionIds: [] });
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [permissionFilter, setPermissionFilter] = useState("");
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [visibilityFilter, setVisibilityFilter] = useState("");
  const [expandedGroupModules, setExpandedGroupModules] = useState({});

  const load = () => {
    api
      .get("/groups")
      .then((res) => setItems(res.data))
      .catch(() => setItems([]));
    api
      .get("/permissions")
      .then((res) => setPermissions(res.data))
      .catch(() => setPermissions([]));
  };

  useEffect(() => {
    load();
  }, []);

  const permissionModules = useMemo(
    () => groupPermissionsByModule(permissions, permissionFilter),
    [permissions, permissionFilter],
  );

  const visibilityPermissionModules = useMemo(
    () => groupPermissionsByModule(permissions, visibilityFilter),
    [permissions, visibilityFilter],
  );

  const visiblePermissionSearchCount = useMemo(
    () => visibilityPermissionModules.reduce((total, group) => total + group.permissions.length, 0),
    [visibilityPermissionModules],
  );

  const filteredGroups = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return items;
    return items.filter((group) => {
      const permissionNames = (group.permissions || [])
        .map((perm) => formatPermissionName(perm.name).toLowerCase());
      return (
        group.name.toLowerCase().includes(term) ||
        permissionNames.some((name) => name.includes(term))
      );
    });
  }, [items, searchTerm]);

  const toggleGroupModule = (groupId, moduleName) => {
    const key = `${groupId}::${moduleName}`;
    setExpandedGroupModules((current) => ({ ...current, [key]: !current[key] }));
  };

  const togglePermission = (id) => {
    setForm((current) => {
      const has = current.permissionIds.includes(id);
      return {
        ...current,
        permissionIds: has
          ? current.permissionIds.filter((permId) => permId !== id)
          : [...current.permissionIds, id],
      };
    });
  };

  const toggleModulePermissions = (modulePermissions) => {
    const ids = modulePermissions.map((permission) => permission.id);
    setForm((current) => {
      const allSelected = ids.every((id) => current.permissionIds.includes(id));
      return {
        ...current,
        permissionIds: allSelected
          ? current.permissionIds.filter((id) => !ids.includes(id))
          : Array.from(new Set([...current.permissionIds, ...ids])),
      };
    });
  };

  const handleChange = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm({ name: "", permissionIds: [] });
    setPermissionFilter("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      if (editingId) {
        await api.put(`/groups/${editingId}`, form);
      } else {
        await api.post("/groups", form);
      }
      closeModal();
      load();
    } catch (error) {
      const msg = error.response?.data?.error || "Erro ao salvar grupo";
      alert(msg);
    }
  };

  const handleEdit = (group) => {
    setEditingId(group.id);
    setForm({
      name: group.name,
      permissionIds: group.permissions.map((permission) => permission.id),
    });
    setPermissionFilter("");
    setModalOpen(true);
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ name: "", permissionIds: [] });
    setPermissionFilter("");
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Remover grupo?")) {
      try {
        await api.delete(`/groups/${id}`);
        load();
      } catch (error) {
        const msg = error.response?.data?.error || "Erro ao remover grupo";
        alert(msg);
      }
    }
  };

  const setPermissionVisibility = async (permission, visible) => {
    if (!isMasterUser || visibilitySaving) return;
    setVisibilitySaving(true);
    try {
      const response = await api.patch("/permissions/visibility", {
        ids: [permission.id],
        visible,
      });
      setPermissions(response.data);
    } catch (error) {
      const msg = error.response?.data?.error || "Erro ao atualizar visibilidade da permissão";
      alert(msg);
    } finally {
      setVisibilitySaving(false);
    }
  };

  const togglePermissionVisibility = (permission) => {
    setPermissionVisibility(permission, permission.visible === false);
  };

  const totalGroups = items.length;
  const totalPermissions = permissions.filter((permission) => permission.visible !== false).length;
  const hiddenPermissions = permissions.filter((permission) => permission.visible === false).length;
  const averagePermissions = totalGroups
    ? Math.round(
        items.reduce((acc, group) => acc + (group.permissions?.length || 0), 0) /
          totalGroups
      )
    : 0;
  const highlightedGroup = useMemo(() => {
    if (!items.length) return null;
    return items
      .slice()
      .sort(
        (a, b) => (b.permissions?.length || 0) - (a.permissions?.length || 0)
      )[0];
  }, [items]);

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
            Governança de acesso
          </p>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-semibold text-slate-900">
              Grupo/Permissões
            </h1>
            <InfoTooltip label="Organize permissões e responsabilidades rapidamente." />
          </div>
        </div>
        <button
          onClick={openNew}
          className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2 text-sm font-medium text-white shadow-lg transition hover:bg-blue-700"
        >
          <FaPlus />
          Novo grupo
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-extrabold text-blue-700">
          <FaLayerGroup /> {totalGroups} grupos
        </span>
        <span className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1.5 text-xs font-extrabold text-emerald-700">
          <FaShieldAlt /> {totalPermissions} permissões
        </span>
        {isMasterUser && hiddenPermissions > 0 && (
          <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-500">
            {hiddenPermissions} ocultas
          </span>
        )}
      </div>

      {isMasterUser && (
        <details className="group rounded-2xl border border-slate-200 bg-white shadow-sm">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-extrabold text-slate-700">
              <FaShieldAlt className="text-blue-600" /> Catálogo de permissões
              <InfoTooltip label="Área avançada do usuário master para mostrar ou ocultar permissões." />
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-500">
              {totalPermissions} visíveis
            </span>
          </summary>
          <div className="border-t border-slate-100 p-4">

          <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full md:max-w-md">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={visibilityFilter}
                onChange={(event) => setVisibilityFilter(event.target.value)}
                placeholder="Filtrar permissões"
                className="w-full rounded-full border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
              {visiblePermissionSearchCount} permissões encontradas
            </span>
          </div>

          <div className="mt-4 max-h-[430px] space-y-4 overflow-y-auto pr-1">
            {visibilityPermissionModules.map((group) => (
              <section key={group.module} className="rounded-2xl border border-slate-200 bg-slate-50/60 p-3">
                <div className="mb-2 flex items-center justify-between gap-2 px-1">
                  <h3 className="text-xs font-extrabold uppercase tracking-[0.12em] text-slate-600">{group.module}</h3>
                  <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-slate-400">{group.permissions.length}</span>
                </div>
                <div className="space-y-3">
                  {(group.sections || [{ section: "Permissões", permissions: group.permissions }]).map((sectionGroup) => (
                    <div key={sectionGroup.section} className={group.sections?.length > 1 ? "rounded-xl border border-slate-200 bg-white/70 p-2.5" : ""}>
                      {group.sections?.length > 1 && (
                        <div className="mb-2 flex items-center gap-2">
                          <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                          <h4 className="text-[11px] font-extrabold uppercase tracking-[0.09em] text-slate-500">{sectionGroup.section}</h4>
                          <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-400">{sectionGroup.permissions.length}</span>
                        </div>
                      )}
                      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                        {sectionGroup.permissions.map((permission) => {
                          const visible = permission.visible !== false;
                          return (
                            <label
                              key={permission.id}
                              className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 text-sm transition ${
                                visible
                                  ? "border-emerald-200 bg-white text-slate-800"
                                  : "border-slate-200 bg-slate-100 text-slate-400"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={visible}
                                disabled={visibilitySaving}
                                onChange={(event) => setPermissionVisibility(permission, event.target.checked)}
                                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:cursor-wait disabled:opacity-60"
                              />
                              <span className={`font-medium ${visible ? "" : "line-through"}`}>
                                {formatPermissionName(permission.name)}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
            {!visibilityPermissionModules.length && (
              <p className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center text-sm text-slate-500">
                Nenhuma permissão encontrada.
              </p>
            )}
          </div>
          </div>
        </details>
      )}

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:max-w-md">
          <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Buscar por nome ou permissão"
            className="w-full rounded-full border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span>Resultados</span>
          <span className="inline-flex min-w-[2rem] justify-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
            {filteredGroups.length}
          </span>
          <InfoTooltip
            label={
              filteredGroups.length === 1
                ? 'Um grupo corresponde aos filtros.'
                : `${filteredGroups.length} grupos correspondem aos filtros.`
            }
          />
        </div>
      </div>

      <section className="grid gap-4 lg:grid-cols-2">
        {filteredGroups.length ? (
          filteredGroups.map((group) => (
            <article
              key={group.id}
              className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">{group.name}</h2>
                  <p className="text-xs uppercase tracking-widest text-slate-400">
                    {(group.permissions?.length || 0)} permissões vinculadas
                  </p>
                </div>
                <div className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-600">
                  ID #{String(group.id).padStart(3, "0")}
                </div>
              </div>

              <div className="mt-4 space-y-2">
                {group.permissions && group.permissions.length ? (
                  groupPermissionsByModule(group.permissions).map((moduleGroup) => {
                    const expansionKey = `${group.id}::${moduleGroup.module}`;
                    const expanded = Boolean(expandedGroupModules[expansionKey]);
                    return (
                      <div key={moduleGroup.module} className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                        <button
                          type="button"
                          onClick={() => toggleGroupModule(group.id, moduleGroup.module)}
                          aria-expanded={expanded}
                          className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-xs font-extrabold text-slate-700 transition hover:bg-blue-50"
                        >
                          <span>{moduleGroup.module}</span>
                          <span className="flex items-center gap-2">
                            <span className="rounded-full bg-white px-2 py-0.5 text-[10px] text-blue-600">{moduleGroup.permissions.length}</span>
                            <span className={`text-sm text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`}>⌄</span>
                          </span>
                        </button>
                        {expanded && (
                          <div className="space-y-3 border-t border-slate-200 bg-white p-2.5">
                            {(moduleGroup.sections || [{ section: "Permissões", permissions: moduleGroup.permissions }]).map((sectionGroup) => (
                              <div key={sectionGroup.section}>
                                {moduleGroup.sections?.length > 1 && (
                                  <div className="mb-1.5 flex items-center gap-2 px-1">
                                    <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                                    <span className="text-[10px] font-extrabold uppercase tracking-[0.09em] text-slate-500">{sectionGroup.section}</span>
                                  </div>
                                )}
                                <div className="grid gap-1.5 md:grid-cols-2">
                                  {sectionGroup.permissions.map((permission) => (
                                    <div key={permission.id} className="flex items-start gap-2 rounded-xl bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
                                      <FaShieldAlt className="mt-0.5 shrink-0 text-blue-500" />
                                      <span>{formatPermissionName(permission.name)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
                    Sem permissões
                  </span>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={() => handleEdit(group)}
                  className="inline-flex items-center gap-2 rounded-full border border-blue-200 px-4 py-2 text-sm font-medium text-blue-600 transition hover:border-blue-300 hover:bg-blue-50"
                >
                  <FaEdit />
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(group.id)}
                  className="inline-flex items-center gap-2 rounded-full border border-rose-200 px-4 py-2 text-sm font-medium text-rose-600 transition hover:border-rose-300 hover:bg-rose-50"
                >
                  <FaTrash />
                  Remover
                </button>
              </div>
            </article>
          ))
        ) : (
          <div className="col-span-full rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500">
            Nenhum grupo encontrado. Ajuste os filtros ou cadastre um novo grupo para começar.
          </div>
        )}
      </section>

      <Modal open={modalOpen} onClose={closeModal} panelClassName="bg-white text-slate-900 dark:bg-white dark:text-slate-900">
        <div className="space-y-6">
          <header className="space-y-1">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-semibold text-slate-900">
                {editingId ? "Editar grupo" : "Novo grupo"}
              </h2>
              <InfoTooltip label="As permissões estão organizadas por módulo para facilitar a configuração." />
            </div>
          </header>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="group-name" className="text-sm font-medium text-slate-700">
                Nome do grupo
              </label>
              <input
                id="group-name"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Ex.: Backoffice Financeiro"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                required
              />
            </div>

            <div className="space-y-3">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    Permissões selecionadas ({form.permissionIds.length})
                  </p>
                  <p className="text-xs text-slate-600">
                    Toque para adicionar ou remover permissões deste grupo.
                  </p>
                </div>
                <div className="relative w-full lg:max-w-xs">
                  <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={permissionFilter}
                    onChange={(event) => setPermissionFilter(event.target.value)}
                    placeholder="Filtrar permissões"
                    className="w-full rounded-full border border-slate-300 bg-white py-2 pl-10 pr-4 text-sm text-slate-900 shadow-sm placeholder:text-slate-500 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
              </div>

              <div className="max-h-[420px] space-y-4 overflow-y-auto pr-1">
                {permissionModules.length ? (
                  permissionModules.map((group) => (
                    <section key={group.module} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <span className="text-xs font-extrabold uppercase tracking-[0.12em] text-slate-600">{group.module}</span>
                        <button
                          type="button"
                          onClick={() => toggleModulePermissions(group.permissions)}
                          className="rounded-full bg-white px-2.5 py-1 text-[10px] font-extrabold text-blue-600 shadow-sm"
                        >
                          {group.permissions.every((permission) => form.permissionIds.includes(permission.id)) ? "Limpar" : "Selecionar módulo"}
                        </button>
                      </div>
                      <div className="space-y-3">
                        {(group.sections || [{ section: "Permissões", permissions: group.permissions }]).map((sectionGroup) => (
                          <div
                            key={sectionGroup.section}
                            className={group.sections?.length > 1 ? "rounded-xl border border-slate-200 bg-white/70 p-2.5" : ""}
                          >
                            {group.sections?.length > 1 && (
                              <div className="mb-2 flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                                  <span className="text-[11px] font-extrabold uppercase tracking-[0.09em] text-slate-500">{sectionGroup.section}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => toggleModulePermissions(sectionGroup.permissions)}
                                  className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-blue-600 transition hover:bg-blue-50"
                                >
                                  {sectionGroup.permissions.every((permission) => form.permissionIds.includes(permission.id)) ? "Limpar área" : "Selecionar área"}
                                </button>
                              </div>
                            )}
                            <div className="grid gap-2 md:grid-cols-2">
                              {sectionGroup.permissions.map((permission) => {
                                const selected = form.permissionIds.includes(permission.id);
                                return (
                                  <div
                                    key={permission.id}
                                    className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${
                                      selected
                                        ? "border-blue-600 bg-blue-600 text-white"
                                        : "border-slate-200 bg-white text-slate-700 hover:border-blue-300"
                                    }`}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => togglePermission(permission.id)}
                                      aria-pressed={selected}
                                      className="flex min-w-0 flex-1 items-center justify-between gap-2 text-left"
                                    >
                                      <span className={`${permission.visible === false ? "line-through opacity-70" : ""}`}>
                                        {formatPermissionName(permission.name)}
                                      </span>
                                      {selected && <FaShieldAlt className="shrink-0 text-white" />}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  ))
                ) : (
                  <p className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    Nenhuma permissão corresponde ao filtro informado.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-blue-700"
              >
                {editingId ? "Atualizar grupo" : "Criar grupo"}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}
